import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { PageSection } from 'adc-ui-components';

import AccountInfoPushdown from '../AccountInfoPushdown';

import { getReturnObject, formatDate } from '../../helpers/returns';
import { getHasXfiPods } from '../../helpers/order';
import { transformServices } from '../../helpers/services';

import DeviceObject from '../../components/DeviceObject';
import ReturnStatusIndicator from '../../components/ReturnStatusIndicator';
import {
  RETURNS_CONFIRMATION_ID,
  RETURNS_ORDER_DETAIL,
  RETURNS_SELECT_DEVICE,
  subst,
} from '../../helpers/routes';

const renderReturnDevices = (devices, orderId) => (
  devices.map(device => (
    <div className="card-group__item" key={`${orderId}${device.serialNumber}`}>
      <div className="card card--secondary">
        <div className="card__content">
          <DeviceObject device={device} showSummary />
        </div>
      </div>
    </div>
  ))
);

const renderReturnItemTitle = (returnItem) => {
  if (returnItem.returnInitiation.returnSource === 'MyAccount') {
    return (
      <p className="body1">
        {`Return #${returnItem.returnOrderId}`}
      </p>
    );
  }

  return (
    <p className="body1">
      {`Return Initiated on ${formatDate(new Date(returnItem.returnInitiation.returnInitiationDate))}`}
    </p>
  );
};

const renderReturns = (returns) => {
  const returnIsCurrent = returnOrderStatus => returnOrderStatus === 'Initiated' || returnOrderStatus === 'In-Progress';

  const returnItems = returns.reduce((result, returnItem) => {
    // Do not display SIK returns on status page
    if (returnItem.returnInitiation.returnSource === 'SIKLabel') {
      return result;
    }

    const newReturnObject = Object.assign({}, returnItem);
    // if a device isn't found on the account, we assume its returned
    const hasReturnedDevice = (
      newReturnObject.returnInitiation.devices.some(device => !device.deviceFound)
    );
    const isXfiPods = getHasXfiPods(newReturnObject.returnInitiation.devices);

    const isCurrentReturn = (
      (!hasReturnedDevice && returnIsCurrent(returnItem.returnOrderStatus))
      || (isXfiPods && returnIsCurrent(returnItem.returnOrderStatus))
    );

    if (isCurrentReturn) {
      result.currentReturns.push({
        ...newReturnObject,
        isXfiPods,
        currentReturn: true,
      });
    } else {
      result.pastReturns.push({
        ...newReturnObject,
        isXfiPods,
        returnOrderStatus: newReturnObject.returnOrderStatus === 'In-Progress' ? 'Completed' : newReturnObject.returnOrderStatus,
      });
    }

    return result;
  }, {
    currentReturns: [],
    pastReturns: [],
  });

  return [].concat(returnItems.currentReturns, returnItems.pastReturns).map((returnItem) => {
    const manageLinkText = returnItem.currentReturn ? 'Manage Return' : 'Return Details';

    const {
      returnOrderId,
      returnOrderStatus,
      returnInitiation,
      isXfiPods,
    } = returnItem;
    const { devices, returnInitiationDate } = returnInitiation;

    return (
      <div className="card-group mb12" key={returnOrderId}>
        <div className="card-group__item">
          <div className="card card--action-right-at-768 card--flex-start">
            <div className="card__content">
              <div className="hgroup">
                {renderReturnItemTitle(returnItem)}
                <ReturnStatusIndicator
                  status={returnOrderStatus}
                  useDate={isXfiPods}
                  date={returnInitiationDate}
                />
              </div>
            </div>
            <div className="card__action">
              <Link
                to={subst(RETURNS_ORDER_DETAIL, { id: returnOrderId })}
              >
                { manageLinkText }
              </Link>
              {process.env.NODE_ENV !== 'production'
                && (
                  <>
                    <br />
                    <Link
                      to={subst(RETURNS_CONFIRMATION_ID, { id: returnOrderId })}
                    >
                      View as Confirmation
                    </Link>
                  </>
                )
              }
            </div>
          </div>
        </div>
        {renderReturnDevices(devices, returnOrderId)}
      </div>
    );
  });
};

export const ReturnsListComponent = ({
  returns,
  accountNumber,
  serviceAddress,
  hasLinkedAccounts,
}) => (
  <>
    <div className="returns-account-info">
      <AccountInfoPushdown
        accountNumber={accountNumber}
        hasLinkedAccounts={hasLinkedAccounts}
        serviceAddress={serviceAddress}
      />
    </div>
    <PageSection className="ui-grey">
      <div className="flex-content flex-content--at-768">
        <div className="flex-content__item">
          <div className="hgroup">
            <h1 className="heading2">Device Management Center</h1>
            <p>
              Start a new return, track your current returns, and view past returns.
            </p>
          </div>
        </div>
        <div className="flex-content__item">
          <div className="action action--right">
            <Link className="button" to={RETURNS_SELECT_DEVICE}>Start A New Return</Link>
          </div>
        </div>
      </div>


      {!returns.length && <p>No Returns Found</p>}
      {!!returns.length && renderReturns(returns)}

      <div className="card card--default">
        <div className="card__content">
          <p>
            If you recently returned a device to an Xfinity Store or a UPS Store, please allow up
            to 45 days for your return to be reflected on your account.
          </p>
        </div>
      </div>
    </PageSection>
  </>
);

const mapStateToProps = ({
  account: { data: accountData } = {},
  auth: { macaroon: { accounts = [] } = {} } = {},
  devices: { services = {} } = {},
  returns: { returns = [] } = {},
}) => {
  const { devices: transformedDevices = {} } = transformServices(
    {
      dsmDevices: services,
      returns: { returns },
      accountData,
    },
  );
  return {
    returns: returns.map(returnData => getReturnObject(
      returnData,
      { devices: Object.values(transformedDevices) },
    )),
    accountNumber: accountData && accountData.accountNumber,
    serviceAddress: accountData && accountData.serviceAddress,
    hasLinkedAccounts: accounts.length > 1,
  };
};

export default connect(mapStateToProps)(ReturnsListComponent);
